import * as XLSX from "xlsx";   // Round 3a: Export Excel for the DIR
import React, { useMemo, useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import { openDirPdf, downloadDirPdf } from "./dirToPdf";

// Text-only report branding (owner request) — no logo images anywhere.
const BRAND_OPTIONS = [
  { value: "sswhite", label: "SS White" },
  { value: "shukla", label: "Shukla Medical" },
  { value: "sss", label: "Shukla Surgical Support" },
];

export default function PIR({ onBack }) {
  const dialog = useAppDialog();

  // PIR round (Issue 2): India is no longer a PIR option — the report always
  // runs in the approved USA MAFIA Aux context, so the country is a constant
  // rather than user-selectable state. (The Import From NJ / India feature is
  // separate and untouched.)
  const countryCode = "USA";
  const [mode, setMode] = useState("job");
  // Rounds 6 batch — Document Branding (approved Q8).
  const [branding, setBranding] = useState("sswhite");
  // Rounds 7 batch — Part Number mode.
  const [partNumber, setPartNumber] = useState("");

  // Round 3a (owner): exports the DIR as one workbook — a sheet per section
  // shown in the preview (empty sections skipped). SheetJS is already a
  // project dependency; no new packages.
  const exportDirExcel = () => {
    if (!reportData) return;
    const wb = XLSX.utils.book_new();
    const addSheet = (name, rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return;
      XLSX.utils.book_append_sheet(
        wb, XLSX.utils.json_to_sheet(rows),
        name.replace(/[\\/?*\[\]:]/g, " ").slice(0, 31));
    };
    addSheet("DIR Review / Instruction Table", reportData?.instructionRows);
    addSheet("Operator Entries", reportData?.operatorEntriesRows);
    addSheet("Q Final Entries", reportData?.qFinalEntriesRows);
    addSheet("Operator Comments", reportData?.operatorCommentsRows);
    addSheet("Q Final Comments", reportData?.qFinalCommentsRows);
    if (wb.SheetNames.length === 0) return;
    const nm = reportData?.jobPo || reportData?.job || reportData?.partNum || "DIR";
    XLSX.writeFile(wb, `DIR_${nm}.xlsx`);
  };

  const [partRevOptions, setPartRevOptions] = useState([]); // [{rev,revMinor,value,label}]
  const [selectedPartRev, setSelectedPartRev] = useState("");
  // Rounds 8 batch (revised) — PO/Line results fed by the SAME lookup search,
  // plus the selected-job indicator.
  const [poLookupRows, setPoLookupRows] = useState([]);
  const [poPage, setPoPage] = useState(1);
  const [poTotal, setPoTotal] = useState(0);
  const PO_PAGE_SIZE = 100;
  const [selectedLookupJob, setSelectedLookupJob] = useState("");

  const [jobNumber, setJobNumber] = useState("");
  const [poLine, setPoLine] = useState("");

  // Round MAFIA-UAT-1 (2026) — "PO/Line not reading properly" fix. Legacy
  // stores and splits the value with a BACKSLASH ("123456\\2"), but the
  // field is labeled "PO/Line" so users naturally type a forward slash and
  // every lookup failed. Normalize / to \\ once here; the same normalized
  // string is used for validation and every backend call (the Vantage
  // splitter accepts both, but the MafiaTables job-key queries match the
  // stored backslash form literally).
  const normalizedPoLine = poLine.trim().replace(/\//g, "\\");
  const [partSearch, setPartSearch] = useState("");

  const [assemblyOptions, setAssemblyOptions] = useState([]);
  const [assemblyNum, setAssemblyNum] = useState("0");
  const [assemblyEnabled, setAssemblyEnabled] = useState(false);

  const [childOptions, setChildOptions] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");
  const [mainPartNumber, setMainPartNumber] = useState("");

  const [lotOptions, setLotOptions] = useState([]);
  const [selectedLot, setSelectedLot] = useState("");
  const [lotEnabled, setLotEnabled] = useState(false);

  const [jobByPartRows, setJobByPartRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pageError, setPageError] = useState("");

  const canShowLotSelect = lotEnabled && lotOptions.length > 0;
  const currentInputLabel =
    mode === "job" ? "Job Number" : mode === "poLine" ? "PO/Line" : "Part Number";
  const sourceLabel =
    mode === "job"
      ? jobNumber || "No job selected"
      : mode === "poLine"
        ? poLine || "No PO/Line selected"
        : partNumber || "No part selected";

  // Owner UAT — switching to PO/Line auto-loads the default PO feed so the
  // panel isn't empty until the first manual search.
  useEffect(() => {
    if (mode === "poLine" && poLookupRows.length === 0) {
      handleSearchJobByPart();
    }

  }, [mode]);

  const lookupColumns = useMemo(
    () => (jobByPartRows.length > 0 ? Object.keys(jobByPartRows[0]) : []),
    [jobByPartRows]
  );

  // Round 62 — Job Number Lookup UX: dynamic (as-you-type) filtering across
  // the loaded results and click-to-sort on any column header. Purely
  // client-side over the rows the search already returned; the server query
  // is unchanged (its speed fix is the MafiaEntries index script shipped
  // alongside this round).
  const [lookupSort, setLookupSort] = useState({ key: "", dir: 1 });

  // Round 62 follow-up (UAT: "why 2? just remove one"): ONE search box. The
  // Part input both runs the server lookup (Search / Enter) AND live-filters
  // the currently loaded results as you type — no separate filter field.
  const lookupViewRows = useMemo(() => {
    let out = jobByPartRows;
    const q = partSearch.trim().toUpperCase();
    if (q) {
      out = out.filter((row) =>
        Object.values(row || {}).some((v) =>
          String(v ?? "").toUpperCase().includes(q)
        )
      );
    }
    if (lookupSort.key) {
      const { key, dir } = lookupSort;
      out = [...out].sort((a, b) => {
        const av = a?.[key];
        const bv = b?.[key];
        const an = Number(av);
        const bn = Number(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn) && String(av).trim() !== "" && String(bv).trim() !== "") {
          return (an - bn) * dir;
        }
        return String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" }) * dir;
      });
    }
    return out;
  }, [jobByPartRows, partSearch, lookupSort]);

  function toggleLookupSort(key) {
    setLookupSort((prev) =>
      prev.key === key ? { key, dir: prev.dir * -1 } : { key, dir: 1 }
    );
  }

  function clearPreview() {
    setReportData(null);
    setShowPreview(false);
  }

  // Round MAFIA-UAT-4 (2026) — UAT: "Clear Preview" only discarded an
  // already-generated report and sat disabled otherwise, so with a filled
  // form and no preview it appeared to do nothing. It is now a full CLEAR:
  // preview + both source inputs + assembly/child/lot context + any error.
  function clearAll() {
    clearPreview();
    setJobNumber("");
    setPoLine("");
    setPageError("");
    // Rounds 6–8 batch — new-mode state clears with everything else.
    setPartNumber("");
    setPartRevOptions([]);
    setSelectedPartRev("");
    setPoLookupRows([]);
    setPoPage(1);
    setPoTotal(0);
    setSelectedLookupJob("");
    resetContextOnly();
  }

  function resetContextOnly() {
    setAssemblyOptions([]);
    setAssemblyNum("0");
    setAssemblyEnabled(false);
    setChildOptions([]);
    setSelectedChild("");
    setMainPartNumber("");
    setLotOptions([]);
    setSelectedLot("");
    setLotEnabled(false);
  }

  async function handleJobNumberLeave() {
    const trimmedJob = jobNumber.trim();

    setAssemblyOptions([]);
    setChildOptions([]);
    clearPreview();

    if (!trimmedJob) {
      resetContextOnly();
      return;
    }
    await loadJobContext(trimmedJob);
  }

  // Rounds 8 batch — the exact pipeline the Job field runs on blur
  // (assemblies → lots → main part → children → DIR build), extracted so a
  // lookup-row click can auto-fill and load without duplicating the flow.
  async function loadJobContext(trimmedJob) {
    try {
      setLoading(true);
      setPageError("");

      const [assemblies, lots] = await Promise.all([
        getAssemblies({ jobNum: trimmedJob }),
        getLots({ jobNum: trimmedJob }),
      ]);

      const assemblyValues = (assemblies || [])
        .map((row) => {
          const firstValue = Object.values(row || {})[0];
          return String(firstValue ?? "");
        })
        .filter(Boolean);

      const firstAssembly = assemblyValues.length > 0 ? assemblyValues[0] : "0";

      setAssemblyOptions(assemblyValues);
      setAssemblyNum(firstAssembly);
      setAssemblyEnabled(assemblyValues.length > 1);

      if ((lots || []).length > 0) {
        const normalizedLots = lots.map((x) => String(x));
        setLotOptions(normalizedLots);
        setSelectedLot("");
        setLotEnabled(true);
      } else {
        setLotOptions([]);
        setSelectedLot("");
        setLotEnabled(false);
      }

      const seq = Number(firstAssembly || "0");
      const fetchedMainPart = await getPartNumber({
        jobNum: trimmedJob,
        seq,
      });

      const normalizedMainPart = String(fetchedMainPart || "").trim();
      setMainPartNumber(normalizedMainPart);

      const children = await getChildren(normalizedMainPart || "");
      const normalizedChildren = (children || [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      const mergedChildren = normalizedMainPart
        ? [normalizedMainPart, ...normalizedChildren.filter((x) => x !== normalizedMainPart)]
        : normalizedChildren;

      setChildOptions(mergedChildren);

      const defaultChild = mergedChildren.length > 0 ? mergedChildren[0] : normalizedMainPart;
      setSelectedChild(defaultChild || "");

      await buildPirForJob({
        jobNum: trimmedJob,
        seq,
        partNumOverride: defaultChild || normalizedMainPart,
        orig: true,
        lot: 0,
        country: countryCode,
      });
    } catch (error) {
      console.error("Failed to load job context:", error);
      const message = describePirError(error, "Failed to load job information.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAssemblyChange(nextValue) {
    setAssemblyNum(nextValue);

    if (mode !== "job" || !jobNumber.trim()) {
      return;
    }

    try {
      setLoading(true);
      setPageError("");

      const fetchedMainPart = await getPartNumber({
        jobNum: jobNumber.trim(),
        seq: Number(nextValue || 0),
      });

      const normalizedMainPart = String(fetchedMainPart || "").trim();
      setMainPartNumber(normalizedMainPart);

      const children = await getChildren(normalizedMainPart || "");
      const normalizedChildren = (children || [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      const mergedChildren = normalizedMainPart
        ? [normalizedMainPart, ...normalizedChildren.filter((x) => x !== normalizedMainPart)]
        : normalizedChildren;

      setChildOptions(mergedChildren);

      const defaultChild = mergedChildren.length > 0 ? mergedChildren[0] : normalizedMainPart;
      setSelectedChild(defaultChild || "");

      let lot = 0;
      if (lotEnabled && selectedLot && lotOptions.includes(selectedLot)) {
        lot = Number(selectedLot);
      }

      await buildPirForJob({
        jobNum: jobNumber.trim(),
        seq: Number(nextValue || 0),
        partNumOverride: defaultChild || normalizedMainPart,
        orig: true,
        lot,
        country: countryCode,
      });
    } catch (error) {
      console.error("Failed to refresh child list:", error);
      const message = describePirError(error, "Failed to refresh child part list.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleChildChange(nextValue) {
    setSelectedChild(nextValue);

    if (mode !== "job" || !jobNumber.trim()) {
      return;
    }

    try {
      setLoading(true);

      let lot = 0;
      if (lotEnabled && selectedLot && lotOptions.includes(selectedLot)) {
        lot = Number(selectedLot);
      }

      const orig =
        String(nextValue || "").trim().toLowerCase() ===
        String(mainPartNumber || "").trim().toLowerCase();

      await buildPirForJob({
        jobNum: jobNumber.trim(),
        seq: Number(assemblyNum || 0),
        partNumOverride: nextValue,
        orig,
        lot,
        country: countryCode,
      });
    } catch (error) {
      console.error("Failed to refresh PIR by child:", error);
      const message = describePirError(error, "Failed to refresh DIR by child part.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLoading(false);
    }
  }

  async function handleLotChange(nextValue) {
    setSelectedLot(nextValue);

    const activeSource = mode === "job" ? jobNumber.trim() : normalizedPoLine;
    if (!activeSource) {
      return;
    }

    try {
      setLoading(true);

      let lot = 0;
      if (nextValue && lotOptions.includes(nextValue)) {
        lot = Number(nextValue);
      }

      if (mode === "job") {
        const orig =
          String(selectedChild || "").trim().toLowerCase() ===
          String(mainPartNumber || "").trim().toLowerCase();

        await buildPirForJob({
          jobNum: jobNumber.trim(),
          seq: Number(assemblyNum || 0),
          partNumOverride: selectedChild,
          orig,
          lot,
          country: countryCode,
        });
      } else {
        await buildPirForPoLine({
          jobNum: normalizedPoLine,
          seq: Number(assemblyNum || 0),
          orig: true,
          lot,
          country: countryCode,
        });
      }
    } catch (error) {
      console.error("Failed to refresh PIR by lot:", error);
      const message = describePirError(error, "Failed to refresh DIR by lot.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLoading(false);
    }
  }

  // Owner request — server-side pagination for PO lines (no 100-row cap).
  async function fetchPoPage(page) {
    try {
      setLookupLoading(true);
      const resp = await api.post("vantage/po-line-search", {
        search: partSearch.trim(),
        page,
        pageSize: PO_PAGE_SIZE,
      });
      const d = resp.data;
      const rows = Array.isArray(d) ? d : (d?.rows ?? []);
      const total = Array.isArray(d) ? d.length : (d?.total ?? rows.length);
      setPoLookupRows(rows);
      setPoTotal(Number(total) || 0);
      setPoPage(Array.isArray(d) ? 1 : (d?.page ?? page));
    } catch (error) {
      console.error("PO/Line search failed:", error);
      setPoLookupRows([]);
      setPoTotal(0);
    } finally {
      setLookupLoading(false);
    }
  }

  async function handleSearchJobByPart() {
    try {
      setLookupLoading(true);
      setPageError("");
      // Owner request (final) — the lookup follows the SELECTED print mode:
      // "PO/Line" searches PO detail lines; "By Job Number" (and Part Number)
      // searches jobs. One focused result list at a time.
      if (mode === "poLine") {
        setJobByPartRows([]);
        setLookupSort({ key: "", dir: 1 });
        await fetchPoPage(1);
      } else {
        setPoLookupRows([]);
        const rows = await getJobNumberByPart({ partNum: partSearch.trim() });
        setJobByPartRows(rows || []);
        setLookupSort({ key: "", dir: 1 });
      }
    } catch (error) {
      console.error("Failed to search job by part:", error);
      const message = describePirError(error, "Failed to search job numbers by part.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLookupLoading(false);
    }
  }

  // Rounds 8 batch — clicking a Job-lookup row selects that job (switches to
  // Job mode, fills the field, and runs the exact same load pipeline as
  // typing + leaving the field).
  async function handleSelectLookupJob(row) {
    const job = String(row?.Job ?? row?.JobNum ?? row?.["Job Num"] ?? "").trim();
    if (!job) return;
    setMode("job");
    setSelectedLookupJob(job);
    setJobNumber(job);
    setPoLine("");
    setAssemblyOptions([]);
    setChildOptions([]);
    clearPreview();
    await loadJobContext(job);
  }

  // Rounds 7 batch — Part Number mode: load the RELEASED revisions for the
  // part (Digital Inspection option-4 rule — newest released first) plus any
  // part-only inspection runs (their run number is the Lot, exactly how the
  // DI part workflow stores them under JobNum = part).
  async function handleLoadPartRevisions() {
    const pn = partNumber.trim();
    if (!pn) return;
    try {
      setLoading(true);
      setPageError("");
      const resp = await api.post("mafia/part-released-revisions", { partNumber: pn });
      const rows = resp.data || [];
      const opts = rows.map((r) => {
        const rev = Number(r.Rev ?? r.rev ?? 0);
        const revMinor = Number(r.RevMinor ?? r.revMinor ?? 0);
        return { rev, revMinor, value: `${rev}-${revMinor}`, label: `Rev ${rev}-${revMinor}` };
      });
      setPartRevOptions(opts);
      setSelectedPartRev(opts.length > 0 ? opts[0].value : "");
      if (opts.length === 0) {
        await dialog.warning(`No released MAFIA table was found for part ${pn}.`);
      }
      const lots = await getLots({ jobNum: pn });
      if ((lots || []).length > 0) {
        setLotOptions(lots.map((x) => String(x)));
        setSelectedLot("");
        setLotEnabled(true);
      } else {
        setLotOptions([]);
        setSelectedLot("");
        setLotEnabled(false);
      }
    } catch (error) {
      console.error("Failed to load released revisions:", error);
      const message = describePirError(error, "Failed to load released revisions.");
      setPageError(message);
      await dialog.error(message);
    } finally {
      setLoading(false);
    }
  }

  // Rounds 8 batch (revised) — a clicked PO/Line lookup row fills the field.
  function handleSelectPoLineRow(row) {
    const po = String(row?.PO ?? row?.PONum ?? "").trim();
    const line = String(row?.Line ?? row?.POLine ?? "").trim();
    if (!po || !line) return;
    setMode("poLine");
    setPoLine(`${po}\\${line}`);
    clearPreview();
  }

  async function handleGeneratePIR() {
    // Country is fixed to the approved USA context (India option removed
    // from the PIR page) — no country validation needed.
    if (mode === "job") {
      if (jobNumber.trim() === "") {
        await dialog.warning("Please enter a job number before continuing.");
        return;
      }

      let lot = 0;
      if (lotEnabled && selectedLot && lotOptions.includes(selectedLot)) {
        lot = Number(selectedLot);
      }

      const orig =
        String(selectedChild || "").trim().toLowerCase() ===
        String(mainPartNumber || "").trim().toLowerCase();

      try {
        setLoading(true);
        setPageError("");
        await buildPirForJob({
          jobNum: jobNumber.trim(),
          seq: Number(assemblyNum || 0),
          partNumOverride: selectedChild,
          orig,
          lot,
          country: countryCode,
        });
      } catch (error) {
        // Round 58 (PIR fix): this rejection was previously UNCAUGHT — an
        // unhandled promise rejection in the console and no user feedback.
        console.error("Failed to generate PIR:", error);
        const message = describePirError(error, "Failed to generate DIR.");
        setPageError(message);
        await dialog.error(message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mode === "poLine") {
      if (poLine.trim() === "") {
        await dialog.warning("Please enter a po/line before continuing.");
        return;
      }

      if (!normalizedPoLine.includes("\\")) {
        await dialog.warning(
          "Please enter the PO number and line separated by \\ or / — e.g. 123456\\2 or 123456/2."
        );
        return;
      }

      let lot = 0;
      if (lotEnabled && selectedLot && lotOptions.includes(selectedLot)) {
        lot = Number(selectedLot);
      }

      try {
        setLoading(true);
        setPageError("");
        await buildPirForPoLine({
          jobNum: normalizedPoLine,
          seq: Number(assemblyNum || 0),
          orig: true,
          lot,
          country: countryCode,
        });
      } catch (error) {
        console.error("Failed to generate PIR:", error);
        const message = describePirError(error, "Failed to generate DIR.");
        setPageError(message);
        await dialog.error(message);
      } finally {
        setLoading(false);
      }
    }

    // Rounds 7 batch — Part Number mode (approved Q7): DIR from the LATEST
    // (or user-selected) RELEASED MAFIA revision.
    if (mode === "part") {
      const pn = partNumber.trim();
      if (pn === "") {
        await dialog.warning("Please enter a part number before continuing.");
        return;
      }
      const rev = partRevOptions.find((r) => r.value === selectedPartRev) || partRevOptions[0];
      if (!rev) {
        await dialog.warning("No released MAFIA revision is loaded for this part. Click Load next to the part number first.");
        return;
      }
      let lot = 0;
      if (lotEnabled && selectedLot && lotOptions.includes(selectedLot)) {
        lot = Number(selectedLot);
      }
      try {
        setLoading(true);
        setPageError("");
        await buildPirForPart({ partNum: pn, revMajor: rev.rev, revMinor: rev.revMinor, lot, country: countryCode });
      } catch (error) {
        console.error("Failed to generate DIR by part:", error);
        const message = describePirError(error, "Failed to generate DIR.");
        setPageError(message);
        await dialog.error(message);
      } finally {
        setLoading(false);
      }
    }
  }

  async function buildPirForJob({
    jobNum,
    seq,
    partNumOverride,
    orig,
    lot,
    country,
  }) {
    const partNum = String(partNumOverride || "").trim();

    if (partNum === "") {
      await dialog.warning("Could not find job number in Vantage");
      return;
    }

    const origQuantity = await callStage(
      "Job quantity lookup (vantage/quantity)",
      () => getQuantity({ jobNum, seq })
    );

    if (Number(origQuantity) === 0) {
      await dialog.warning(
        "Vantage reports Quantity of 0, please contact Technical Support, x1776"
      );
      return;
    }

    let [revMajor, revMinor] = await callStage(
      "Job revision lookup (mafia/job-revmajor, job-revminor)",
      () => Promise.all([getJobRevMajor(jobNum, seq), getJobRevMinor(jobNum, seq)])
    );

    if (Number(revMajor) === 0) {
      [revMajor, revMinor] = await callStage(
        "Job revision lookup from comments (mafia/job-revmajor-comment, job-revminor-comment)",
        () =>
          Promise.all([
            getJobRevMajorComment(jobNum, seq),
            getJobRevMinorComment(jobNum, seq),
          ])
      );
    }

    if (Number(revMajor) === 0) {
      await dialog.warning(
        "Error in mafia table, or no entries were entered yet. please contact Technical Support, x1776"
      );
      return;
    }

    const [
      mafiaTable,
      instrTable,
      operatorCommentsTable,
      qFinalCommentsTable,
      operatorEntriesTable,
      qFinalEntriesTable,
      partName,
    ] = await callStage(
      "DIR data load (mafia table / instructions / entries / comments / description)",
      () =>
        Promise.all([
          getMafiaTable({ partNumber: partNum, rev: revMajor, revMinor }),
          getPirInstructions({ partNumber: partNum, rev: revMajor, revMinor }),
          getPirComments({ jobNum, revMajor, revMinor, wave: 1, seq, partNum, orig, lot }),
          getPirComments({ jobNum, revMajor, revMinor, wave: 2, seq, partNum, orig, lot }),
          getPirEntries({ jobNum, revMajor, revMinor, wave: 1, seq, partNum, orig, lot }),
          getPirEntries({ jobNum, revMajor, revMinor, wave: 2, seq, partNum, orig, lot }),
          getDescription({ jobNum, seq }),
        ])
    );

    const normalizedMasterRows = normalizeInstructionRows(instrTable, mafiaTable);

    if (normalizedMasterRows.length === 0) {
      await dialog.warning(
        "Error in obtaining DIR instruction table, please contact Technical Support, x1776"
      );
      return;
    }

    const normalizedOperatorEntries = normalizePirEntries(operatorEntriesTable);
    const normalizedQFinalEntries = normalizePirEntries(qFinalEntriesTable);

    const mergedInstructions = mergeInstructionComments(
      normalizedMasterRows,
      operatorCommentsTable || [],
      qFinalCommentsTable || []
    );

    setReportData({
      branding,
      sourceType: "job",
      country,
      jobNumber: jobNum,
      quantity: origQuantity,
      partNum,
      partName,
      revText: `${revMajor}-${revMinor}`,
      instructionRows: mergedInstructions,
      operatorCommentsRows: normalizeCommentRows(operatorCommentsTable),
      qFinalCommentsRows: normalizeCommentRows(qFinalCommentsTable),
      operatorEntriesRows: normalizedOperatorEntries,
      qFinalEntriesRows: normalizedQFinalEntries,
    });

    setShowPreview(true);
  }

  async function buildPirForPoLine({
    jobNum,
    seq,
    orig,
    lot,
    country,
  }) {
    const [partNum, origQuantity] = await callStage(
      "PO/Line lookup (vantage/partnumber-po-line, quantity-po-line)",
      () => Promise.all([getPartNumberPoLine({ jobNum }), getQuantityPoLine({ jobNum })])
    );

    const normalizedPartNum = String(partNum || "").trim();

    if (normalizedPartNum === "") {
      await dialog.warning("Could not find job number in Vantage");
      return;
    }

    if (Number(origQuantity) === 0) {
      await dialog.warning(
        "Vantage reports Quantity of 0, please contact Technical Support, x1776"
      );
      return;
    }

    let [revMajor, revMinor] = await callStage(
      "Job revision lookup (mafia/job-revmajor, job-revminor)",
      () => Promise.all([getJobRevMajor(jobNum, seq), getJobRevMinor(jobNum, seq)])
    );

    if (Number(revMajor) === 0) {
      [revMajor, revMinor] = await callStage(
        "Job revision lookup from comments (mafia/job-revmajor-comment, job-revminor-comment)",
        () =>
          Promise.all([
            getJobRevMajorComment(jobNum, seq),
            getJobRevMinorComment(jobNum, seq),
          ])
      );
    }

    if (Number(revMajor) === 0) {
      await dialog.warning(
        "Error in mafia table, or no entries were entered yet. please contact Technical Support, x1776"
      );
      return;
    }

    const [
      mafiaTable,
      instrTable,
      operatorCommentsTable,
      qFinalCommentsTable,
      operatorEntriesTable,
      qFinalEntriesTable,
      partName,
    ] = await callStage(
      "DIR data load (mafia table / instructions / entries / comments / description)",
      () =>
        Promise.all([
          getMafiaTable({ partNumber: normalizedPartNum, rev: revMajor, revMinor }),
          getPirInstructions({ partNumber: normalizedPartNum, rev: revMajor, revMinor }),
          getPirComments({ jobNum, revMajor, revMinor, wave: 1, seq, partNum: normalizedPartNum, orig, lot }),
          getPirComments({ jobNum, revMajor, revMinor, wave: 2, seq, partNum: normalizedPartNum, orig, lot }),
          getPirEntries({ jobNum, revMajor, revMinor, wave: 1, seq, partNum: normalizedPartNum, orig, lot }),
          getPirEntries({ jobNum, revMajor, revMinor, wave: 2, seq, partNum: normalizedPartNum, orig, lot }),
          getDescriptionPoLine({ jobNum }),
        ])
    );

    const normalizedMasterRows = normalizeInstructionRows(instrTable, mafiaTable);

    if (normalizedMasterRows.length === 0) {
      await dialog.warning(
        "Error in obtaining DIR instruction table, please contact Technical Support, x1776"
      );
      return;
    }

    const normalizedOperatorEntries = normalizePirEntries(operatorEntriesTable);
    const normalizedQFinalEntries = normalizePirEntries(qFinalEntriesTable);

    const mergedInstructions = mergeInstructionComments(
      normalizedMasterRows,
      operatorCommentsTable || [],
      qFinalCommentsTable || []
    );

    setReportData({
      branding,
      sourceType: "poLine",
      country,
      jobNumber: jobNum,
      quantity: origQuantity,
      partNum: normalizedPartNum,
      partName,
      revText: `${revMajor}-${revMinor}`,
      instructionRows: mergedInstructions,
      operatorCommentsRows: normalizeCommentRows(operatorCommentsTable),
      qFinalCommentsRows: normalizeCommentRows(qFinalCommentsTable),
      operatorEntriesRows: normalizedOperatorEntries,
      qFinalEntriesRows: normalizedQFinalEntries,
    });

    setShowPreview(true);
  }

  // Rounds 7 batch — Part Number mode DIR: MASTER inspection data from the
  // LATEST (or selected) RELEASED MAFIA revision — the same source and rule
  // Digital Inspection's Part Number workflow uses — plus any part-only run
  // entries/comments (JobNum = part, seq 0, run = lot). Read-only: printing
  // never creates inspection records.
  async function buildPirForPart({ partNum, revMajor, revMinor, lot, country }) {
    const [
      mafiaTable,
      instrTable,
      operatorCommentsTable,
      qFinalCommentsTable,
      operatorEntriesTable,
      qFinalEntriesTable,
    ] = await callStage(
      "Part DIR data load (mafia table / instructions / entries / comments)",
      () =>
        Promise.all([
          getMafiaTable({ partNumber: partNum, rev: revMajor, revMinor }),
          getPirInstructions({ partNumber: partNum, rev: revMajor, revMinor }),
          getPirComments({ jobNum: partNum, revMajor, revMinor, wave: 1, seq: 0, partNum, orig: true, lot }),
          getPirComments({ jobNum: partNum, revMajor, revMinor, wave: 2, seq: 0, partNum, orig: true, lot }),
          getPirEntries({ jobNum: partNum, revMajor, revMinor, wave: 1, seq: 0, partNum, orig: true, lot }),
          getPirEntries({ jobNum: partNum, revMajor, revMinor, wave: 2, seq: 0, partNum, orig: true, lot }),
        ])
    );

    const normalizedMasterRows = normalizeInstructionRows(instrTable, mafiaTable);
    if (normalizedMasterRows.length === 0) {
      await dialog.warning(
        "Error in obtaining DIR instruction table, please contact Technical Support, x1776"
      );
      return;
    }

    const merged = mergeInstructionComments(
      normalizedMasterRows,
      operatorCommentsTable || [],
      qFinalCommentsTable || []
    );

    setReportData({
      branding,
      sourceType: "part",
      country,
      jobNumber: partNum, // part-only runs store JobNum = part
      quantity: "",
      partNum,
      partName: "",
      revText: `${revMajor}-${revMinor}`,
      instructionRows: merged,
      operatorCommentsRows: normalizeCommentRows(operatorCommentsTable),
      qFinalCommentsRows: normalizeCommentRows(qFinalCommentsTable),
      operatorEntriesRows: normalizePirEntries(operatorEntriesTable),
      qFinalEntriesRows: normalizePirEntries(qFinalEntriesTable),
    });
    setShowPreview(true);
  }


  // Round 62 — legacy-format report output. Replaces the raw print window
  // with a PDF built by src/pages/Mafia/dirToPdf.js (same jsPDF +
  // jspdf-autotable pipeline the EPO purchase-order PDF uses), reproducing
  // the legacy Crystal Reports layout: header band + Job/Part/Rev grid, one
  // section per DNA with the boxed spec strip, tolerance line, and the
  // InProcess / Q-Final entry blocks, "MAFIA FIR REV-J" + page numbers.
  function handleOpenPdf() {
    if (!reportData) return;
    openDirPdf(reportData);
  }

  function handleDownloadPdf() {
    if (!reportData) return;
    downloadDirPdf(reportData);
  }

  return (
    <div className="pir-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        icon="ri-file-list-3-line"
        eyebrow="MAFIA Aux"
        title="Print Digital Inspection Record (DIR)"
        subtitle="Print Digital Inspection Records by job or PO/Line."
        tone="amber"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Print DIR" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="pir-toolbar-main">
                <div className="pir-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>
              </div>

              <div className="pir-toolbar-utility">
                <Button
                  color="primary"
                  className="pir-action-btn pir-utility-btn"
                  onClick={handleGeneratePIR}
                  disabled={loading}
                >
                  <i className="ri-file-chart-line pir-btn-icon me-1" />
                  {loading ? "Preparing DIR..." : "Generate DIR"}
                </Button>

                <Button
                  color="light"
                  className="pir-action-btn pir-utility-btn border"
                  onClick={clearAll}
                  disabled={
                    loading ||
                    (!reportData && !jobNumber.trim() && !poLine.trim() && !pageError)
                  }
                  title="Clear the form and any generated preview"
                >
                  <i className="ri-close-circle-line pir-btn-icon me-1" />
                  Clear
                </Button>

                <div className="pir-info-pill">
                  <span className="pir-info-label">Mode</span>
                  <span className="pir-info-value">{mode === "job" ? "Job" : mode === "poLine" ? "PO/Line" : "Part"}</span>
                </div>

                <div className="pir-info-pill">
                  <span className="pir-info-label">Source</span>
                  <span className="pir-info-value" title={sourceLabel}>
                    {sourceLabel}
                  </span>
                </div>

                <div className="pir-info-pill">
                  <span className="pir-info-label">Preview</span>
                  <span className="pir-info-value">
                    {showPreview ? "Ready" : "Not Generated"}
                  </span>
                </div>
              </div>

              {pageError ? (
                <div className="pir-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <Row className="g-4 mb-4">
                <Col xl={7}>
                  <div className="pir-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">DIR Selection</h5>
                        <p className="text-muted mb-0">
                          Set source type, country, and the job context needed to build the report.
                        </p>
                      </div>
                      <Badge color="light" className="pir-soft-badge text-uppercase">
                        Setup
                      </Badge>
                    </div>

                    <Row className="g-3">
                      <Col md={12}>
                        <Label className="form-label">Print Selection</Label>
                        <div className="pir-choice-grid">
                          <label className={`pir-choice-card ${mode === "job" ? "active" : ""}`}>
                            <input
                              type="radio"
                              checked={mode === "job"}
                              onChange={() => setMode("job")}
                            />
                            <div>
                              <div className="pir-choice-title">By Job Number</div>
                              <div className="pir-choice-copy">
                                Use a job number and assembly context.
                              </div>
                            </div>
                          </label>

                          <label
                            className={`pir-choice-card ${mode === "poLine" ? "active" : ""}`}
                          >
                            <input
                              type="radio"
                              checked={mode === "poLine"}
                              onChange={() => {
                                setMode("poLine");
                                setAssemblyNum("0");
                                clearPreview();
                              }}
                            />
                            <div>
                              <div className="pir-choice-title">PO/Line</div>
                              <div className="pir-choice-copy">
                                Generate from a purchase order and line pair.
                              </div>
                            </div>
                          </label>

                          {/* Rounds 7 batch — Part Number mode: latest released
                              MAFIA data (Digital Inspection option-4 rule). */}
                          <label
                            className={`pir-choice-card ${mode === "part" ? "active" : ""}`}
                          >
                            <input
                              type="radio"
                              checked={mode === "part"}
                              onChange={() => {
                                setMode("part");
                                setAssemblyNum("0");
                                clearPreview();
                              }}
                            />
                            <div>
                              <div className="pir-choice-title">Part Number</div>
                              <div className="pir-choice-copy">
                                Latest released MAFIA data for a part.
                              </div>
                            </div>
                          </label>
                        </div>

                        {/* Rounds 6 batch — Document Branding: the selected logo +
                            the shared address print on the DIR header (Q8). */}
                        <div className="mt-3">
                          <label className="form-label mb-1 d-block">Document Branding</label>
                          <div className="d-flex flex-wrap gap-3">
                            {BRAND_OPTIONS.map((b) => (
                              <label
                                key={b.value}
                                className="d-inline-flex align-items-center gap-2 mb-0"
                                style={{ cursor: "pointer" }}
                              >
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  name="pirBranding"
                                  checked={branding === b.value}
                                  onChange={() => setBranding(b.value)}
                                />
                                <span>{b.label}</span>
                              </label>
                            ))}
                          </div>
                          <div className="text-muted small mt-1">
                            The selected brand name prints on the report header.
                          </div>
                        </div>
                      </Col>

                      {/* Round 62: the Country panel is removed entirely — only the
                          USA database exists now, so there is nothing to choose. */}

                      <Col md={12}>
                        <Label className="form-label">{currentInputLabel}</Label>
                        <InputGroup>
                          <InputGroupText className="bg-light border-end-0">
                            <i
                              className={
                                mode === "job"
                                  ? "ri-briefcase-4-line"
                                  : mode === "poLine"
                                    ? "ri-file-list-3-line"
                                    : "ri-hashtag"
                              }
                            />
                          </InputGroupText>
                          {mode === "job" ? (
                            <Input
                              type="text"
                              value={jobNumber}
                              onChange={(e) => setJobNumber(e.target.value)}
                              onBlur={handleJobNumberLeave}
                              onKeyDown={(e) => {
                                // Round 61 (UX): Enter loads the job — users
                                // shouldn't have to know to click elsewhere.
                                // Blurring fires the existing handler exactly
                                // once (no double load).
                                if (e.key === "Enter") e.target.blur();
                              }}
                              className="border-start-0"
                              placeholder="Enter job number"
                            />
                          ) : mode === "poLine" ? (
                            <Input
                              type="text"
                              value={poLine}
                              onChange={(e) => setPoLine(e.target.value)}
                              className="border-start-0"
                              placeholder="e.g. 123456\2 (or 123456/2)"
                            />
                          ) : (
                            <>
                              <Input
                                type="text"
                                value={partNumber}
                                onChange={(e) => {
                                  setPartNumber(e.target.value);
                                  setPartRevOptions([]);
                                  setSelectedPartRev("");
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") e.target.blur();
                                }}
                                onBlur={handleLoadPartRevisions}
                                className="border-start-0"
                                placeholder="Enter part number"
                              />
                              <Button
                                color="dark"
                                onClick={handleLoadPartRevisions}
                                disabled={loading}
                              >
                                Load
                              </Button>
                            </>
                          )}
                        </InputGroup>
                        {/* Rounds 8 batch — confirmation that a lookup row filled this field. */}
                        {mode === "job" && selectedLookupJob && jobNumber.trim() === selectedLookupJob && (
                          <div className="small text-success mt-1">
                            <i className="ri-checkbox-circle-line align-bottom me-1" />
                            Selected from lookup: Job {selectedLookupJob}
                          </div>
                        )}
                        {/* Rounds 7 batch — released revision picker (Digital
                            Inspection rule: defaults to the LATEST released). */}
                        {mode === "part" && partRevOptions.length > 0 && (
                          <div className="mt-2">
                            <label className="form-label mb-1">Released Revision</label>
                            <Input
                              type="select"
                              value={selectedPartRev}
                              onChange={(e) => setSelectedPartRev(e.target.value)}
                            >
                              {partRevOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </Input>
                            <div className="text-muted small mt-1">
                              Defaults to the latest released revision (Digital Inspection rule).
                            </div>
                          </div>
                        )}
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Assembly Number</Label>
                        <Input
                          type="select"
                          value={assemblyNum}
                          disabled={mode !== "job" || !assemblyEnabled}
                          onChange={(e) => handleAssemblyChange(e.target.value)}
                        >
                          {assemblyOptions.length > 0 ? (
                            assemblyOptions.map((item) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))
                          ) : (
                            <option value="0">0</option>
                          )}
                        </Input>
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Child Part</Label>
                        <Input
                          type="select"
                          value={selectedChild}
                          onChange={(e) => handleChildChange(e.target.value)}
                        >
                          {childOptions.length > 0 ? (
                            childOptions.map((item) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))
                          ) : (
                            <option value="">-- No Child Parts --</option>
                          )}
                        </Input>
                      </Col>

                      <Col md={4}>
                        <Label className="form-label">Lot #</Label>
                        <Input
                          type="select"
                          value={selectedLot}
                          onChange={(e) => handleLotChange(e.target.value)}
                          disabled={!canShowLotSelect}
                        >
                          {!canShowLotSelect && <option value="">-- No Lots --</option>}
                          {canShowLotSelect && (
                            <>
                              <option value="">-- Select Lot --</option>
                              {lotOptions.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </>
                          )}
                        </Input>
                      </Col>
                    </Row>
                  </div>
                </Col>

                <Col xl={5}>
                  <div className="pir-panel-card h-100">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
                      <div>
                        <h5 className="mb-1">Job / PO Line Lookup</h5>
                        <p className="text-muted mb-0">
                          Searches JOBS in Job/Part mode and PO LINES in PO/Line
                          mode — click a row to use it.
                        </p>
                      </div>
                      <Badge color="light" className="pir-soft-badge text-uppercase">
                        Lookup
                      </Badge>
                    </div>

                    <Label className="form-label">Search</Label>
                    <InputGroup className="mb-3">
                      <InputGroupText className="bg-light border-end-0">
                        <i className="ri-search-line" />
                      </InputGroupText>
                      <Input
                        type="text"
                        value={partSearch}
                        onChange={(e) => setPartSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !lookupLoading) handleSearchJobByPart();
                        }}
                        className="border-start-0"
                        placeholder="Part No., description, or PO No."
                      />
                      <Button color="dark" onClick={handleSearchJobByPart} disabled={lookupLoading}>
                        {lookupLoading ? "Searching..." : "Search"}
                      </Button>
                    </InputGroup>

                    {mode !== "poLine" && jobByPartRows.length > 0 && (
                      <div className="text-muted small mb-2">
                        {lookupViewRows.length === jobByPartRows.length
                          ? `${jobByPartRows.length.toLocaleString()} results — typing above narrows them; Search fetches fresh`
                          : `${lookupViewRows.length.toLocaleString()} of ${jobByPartRows.length.toLocaleString()} results match — press Search for a fresh lookup`}
                      </div>
                    )}

                    {mode !== "poLine" && (
                      <div className="pir-table-shell">
                        <div className="pir-table-scroll pir-table-scroll-sm">
                          <Table responsive={false} className="align-middle mb-0 pir-table">
                            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                              <tr>
                                {lookupColumns.length > 0 ? (
                                  lookupColumns.map((key) => (
                                    <th
                                      key={key}
                                      role="button"
                                      onClick={() => toggleLookupSort(key)}
                                      title="Click to sort"
                                      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                                    >
                                      {key}
                                      {lookupSort.key === key ? (
                                        <i
                                          className={`ms-1 align-bottom ${lookupSort.dir === 1 ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                                            }`}
                                        />
                                      ) : (
                                        <i className="ri-expand-up-down-line ms-1 align-bottom text-muted" style={{ fontSize: 12, opacity: 0.5 }} />
                                      )}
                                    </th>
                                  ))
                                ) : (
                                  <th>Results</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {lookupLoading ? (
                                <tr>
                                  <td
                                    colSpan={lookupColumns.length || 1}
                                    className="text-center py-4 text-muted"
                                  >
                                    Searching...
                                  </td>
                                </tr>
                              ) : jobByPartRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={lookupColumns.length || 1}
                                    className="text-center py-4 text-muted"
                                  >
                                    No results.
                                  </td>
                                </tr>
                              ) : lookupViewRows.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={lookupColumns.length || 1}
                                    className="text-center py-4 text-muted"
                                  >
                                    No results match the filter.
                                  </td>
                                </tr>
                              ) : (
                                lookupViewRows.map((row, idx) => {
                                  // Rounds 8 batch — rows are clickable; the
                                  // selected job stays highlighted.
                                  const lookupJobVal = String(row.Job ?? row.JobNum ?? row["Job Num"] ?? "").trim();
                                  const isSelectedLookupRow = !!selectedLookupJob && lookupJobVal === selectedLookupJob;
                                  return (
                                    <tr
                                      key={idx}
                                      className={`pir-data-row ${isSelectedLookupRow ? "table-primary" : ""}`}
                                      role="button"
                                      style={{ cursor: "pointer" }}
                                      title="Click to use this job"
                                      onClick={() => handleSelectLookupJob(row)}
                                    >
                                      {lookupColumns.map((key) => (
                                        <td key={key}>{formatAny(row[key])}</td>
                                      ))}
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Owner request — POs render in the SAME results area
                        (replacing the jobs table in PO/Line mode), with real
                        server-side pagination — no 100-row cap. */}
                    {mode === "poLine" && (
                      <>
                        <div className="d-flex align-items-center justify-content-between text-muted small mb-1 flex-wrap gap-1">
                          <span className="fw-semibold">
                            {poTotal.toLocaleString()} PO line{poTotal === 1 ? "" : "s"} — click a row to fill PO/Line
                          </span>
                          <span className="d-inline-flex align-items-center gap-2">
                            <Button size="sm" outline color="secondary" disabled={lookupLoading || poPage <= 1} onClick={() => fetchPoPage(poPage - 1)}>
                              ‹ Prev
                            </Button>
                            <span>Page {poPage} / {Math.max(1, Math.ceil(poTotal / PO_PAGE_SIZE))}</span>
                            <Button size="sm" outline color="secondary" disabled={lookupLoading || poPage * PO_PAGE_SIZE >= poTotal} onClick={() => fetchPoPage(poPage + 1)}>
                              Next ›
                            </Button>
                          </span>
                        </div>
                        <div className="pir-table-shell">
                          <div className="pir-table-scroll pir-table-scroll-sm">
                            <Table responsive={false} className="align-middle mb-0 pir-table">
                              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                  {poLookupRows.length > 0 ? (
                                    Object.keys(poLookupRows[0]).map((key) => (
                                      <th key={key} style={{ whiteSpace: "nowrap" }}>{key}</th>
                                    ))
                                  ) : (
                                    <>
                                      <th>PO</th>
                                      <th>Line</th>
                                      <th>Part</th>
                                      <th>Description</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {lookupLoading ? (
                                  <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">Searching...</td>
                                  </tr>
                                ) : poLookupRows.length === 0 ? (
                                  <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">No PO lines found.</td>
                                  </tr>
                                ) : (
                                  poLookupRows.map((row, idx) => (
                                    <tr
                                      key={idx}
                                      className="pir-data-row"
                                      role="button"
                                      style={{ cursor: "pointer" }}
                                      title="Click to use this PO/Line"
                                      onClick={() => handleSelectPoLineRow(row)}
                                    >
                                      {Object.keys(poLookupRows[0]).map((key) => (
                                        <td key={key}>{formatAny(row[key])}</td>
                                      ))}
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={showPreview}
        toggle={() => setShowPreview(false)}
        size="xl"
        centered
        scrollable
        className="pir-preview-modal"
      >
        <ModalHeader toggle={() => setShowPreview(false)}>
          Print Digital Inspection Record
        </ModalHeader>

        <ModalBody>
          {reportData && (
            <div className="pir-preview-card modal-preview">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
                <div>
                  <h4 className="mb-1">DIR Preview</h4>
                  <p className="text-muted mb-0">
                    Job/PO: {reportData.jobNumber} | Qty: {reportData.quantity} | Part:{" "}
                    {reportData.partNum} | Part Name: {reportData.partName} | Rev:{" "}
                    {reportData.revText}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    color="light"
                    className="pir-action-btn border"
                    onClick={() => setShowPreview(false)}
                  >
                    <i className="ri-close-line pir-btn-icon me-1" /> Close
                  </Button>
                  <Button
                    color="dark"
                    className="pir-action-btn"
                    onClick={handleOpenPdf}
                  >
                    <i className="ri-printer-line pir-btn-icon me-1" /> Print / View PDF
                  </Button>
                  <Button
                    color="primary"
                    className="pir-action-btn"
                    onClick={handleDownloadPdf}
                  >
                    <i className="ri-download-2-line pir-btn-icon me-1" /> Download PDF
                  </Button>
                  <Button color="success" className="ms-2" onClick={exportDirExcel} disabled={!reportData}>
                    <i className="ri-file-excel-2-line pir-btn-icon me-1" /> Export Excel
                  </Button>
                </div>
              </div>

              <SectionTable title="DIR Review / Instruction Table" rows={reportData.instructionRows} />
              <SectionTable title="Operator Entries" rows={reportData.operatorEntriesRows} />
              <SectionTable title="Q Final Entries" rows={reportData.qFinalEntriesRows} />
              <SectionTable title="Operator Comments" rows={reportData.operatorCommentsRows} />
              <SectionTable title="Q Final Comments" rows={reportData.qFinalCommentsRows} />
            </div>
          )}
        </ModalBody>
      </Modal>

      <style>{`
        .pir-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .pir-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .pir-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .pir-soft-badge {
          border: 1px solid #e9ebec;
          padding: 0.55rem 0.85rem;
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          border-radius: 999px;
        }

        .pir-panel-card,
        .pir-preview-card {
          background: rgba(255,255,255,0.92);
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          padding: 1rem 1.1rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
          height: 100%;
        }

        .modal-preview {
          border: 0;
          box-shadow: none;
          padding: 0;
          background: transparent;
        }

        .pir-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .pir-action-btn:disabled,
          .pir-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .pir-utility-btn {
            min-width: 110px;
          }

        .pir-btn-icon {
          font-size: 0.95rem;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .pir-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .pir-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .pir-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .pir-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .pir-choice-grid {
          display: grid;
          gap: 0.8rem;
        }

        .pir-choice-card {
          display: flex;
          align-items: flex-start;
          gap: 0.9rem;
          border: 1px solid #dbe4ee;
          border-radius: 1rem;
          padding: 1rem;
          background: #fff;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .pir-choice-card:hover {
          background: #f8fbff;
          border-color: #b9d4ff;
        }

        .pir-choice-card.active {
          background: rgba(13, 110, 253, 0.08);
          border-color: rgba(13, 110, 253, 0.28);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        .pir-choice-card input {
          margin-top: 0.2rem;
        }

        .pir-choice-title {
          font-weight: 700;
          color: #212529;
          margin-bottom: 0.2rem;
        }

        .pir-choice-copy {
          color: #878a99;
          font-size: 0.92rem;
        }

        .pir-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .pir-table-scroll {
          max-height: calc(100vh - 260px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

        .pir-table-scroll-sm {
          overflow-x: auto;
          overflow-y: visible;
        }

        .pir-table {
          min-width: 980px;
        }

        .pir-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .pir-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: middle;
          white-space: nowrap;
        }

        .pir-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .pir-data-row:hover {
          background: #f5f9ff;
        }

        .pir-preview-modal .modal-dialog {
          max-width: 95vw;
        }

        @media (max-width: 991.98px) {
          .pir-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .pir-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function SectionTable({ title, rows }) {
  const safeRows = rows || [];
  const columns = safeRows.length > 0 ? Object.keys(safeRows[0]) : [];

  return (
    <div className="mt-4">
      <h5 className="mb-3">{title}</h5>
      <div className="pir-table-shell">
        <div className="pir-table-scroll">
          <Table responsive={false} className="align-middle mb-0 pir-table">
            <thead className="table-light">
              <tr>
                {columns.length > 0 ? (
                  columns.map((key) => <th key={key}>{key}</th>)
                ) : (
                  <th>No Data</th>
                )}
              </tr>
            </thead>
            <tbody>
              {safeRows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length || 1} className="text-center py-4 text-muted">
                    No rows found.
                  </td>
                </tr>
              ) : (
                safeRows.map((row, idx) => (
                  <tr key={idx} className="pir-data-row">
                    {columns.map((key) => (
                      <td key={key}>{formatAny(row[key])}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function normalizeInstructionRows(instrRows, mafiaRows) {
  const baseRows =
    Array.isArray(instrRows) && instrRows.length > 0
      ? instrRows
      : Array.isArray(mafiaRows)
        ? mafiaRows
        : [];

  return baseRows.map((row) => ({
    DNANum: String(row.DNANum ?? row.dnaNum ?? row.DNA ?? row.dna ?? "").trim(),
    PartNum: String(row.PartNum ?? row.partNum ?? "").trim(),
    DimType: String(row.DimType ?? row.dimType ?? row.dimensionType ?? "").trim(),
    UnitMeasurement: String(row.UnitMeasurement ?? row.unitMeasurement ?? row.unit ?? "").trim(),
    Nominal: formatAny(row.Nominal ?? row.nominal),
    Min: formatAny(row.Min ?? row.min),
    Max: formatAny(row.Max ?? row.max),
    MethodOfRecording: String(row.MethodOfRecording ?? row.methodOfRecording ?? "").trim(),
    SpecialInstr: String(row.SpecialInstr ?? row.specialInstr ?? "").trim(),
    SpecialGages: String(row.SpecialGages ?? row.specialGages ?? row.gages ?? "").trim(),
    WIATPNum: String(row.WIATPNum ?? row.wiatpNum ?? row.wiatpNumber ?? "").trim(),
    AddlInfo: String(row.AddlInfo ?? row.addlInfo ?? row.additionalInfo ?? "").trim(),
    MOMOperationNum: formatAny(row.MOMOperationNum ?? row.momOperationNum ?? row.momOpNumber),
    CreatedBy: String(row.CreatedBy ?? row.createdBy ?? "").trim(),
  }));
}

function normalizeCommentRows(rows) {
  return (rows || []).map((row) => ({
    ...row,
    DNANum: String(row.DNANum ?? row.dnaNum ?? row.DNA ?? row.dna ?? "").trim(),
    Comment: String(row.Comment ?? row.comment ?? "").trim(),
  }));
}

function normalizePirEntries(rows) {
  return (rows || []).map((row) => {
    const copy = { ...row };
    const dna = String(copy.DNANum ?? copy.dnaNum ?? copy.DNA ?? copy.dna ?? "").trim();
    if (dna) {
      copy.DNANum = dna;
    }

    const rawValue = String(copy.Value ?? copy.value ?? "").trim();
    if (rawValue === "-777") {
      copy.Value = "PASS";
    } else if (rawValue === "-999") {
      copy.Value = "FAIL";
    } else if (copy.Value == null && copy.value != null) {
      copy.Value = copy.value;
    }

    return copy;
  });
}

function mergeInstructionComments(instrRows, operatorCommentsRows, qFinalCommentsRows) {
  const normalizedOperatorComments = normalizeCommentRows(operatorCommentsRows);
  const normalizedQFinalComments = normalizeCommentRows(qFinalCommentsRows);

  return (instrRows || []).map((row) => {
    const dna = String(row.DNANum ?? "").trim();

    let inProcCom = "";
    let qcCom = "";

    for (const op of normalizedOperatorComments) {
      if (String(op.DNANum ?? "").trim() === dna) {
        inProcCom = String(op.Comment ?? "").trim();
      }
    }

    for (const qc of normalizedQFinalComments) {
      if (String(qc.DNANum ?? "").trim() === dna) {
        qcCom = String(qc.Comment ?? "").trim();
      }
    }

    return {
      ...row,
      InProcCom: inProcCom,
      QCCom: qcCom,
    };
  });
}

function formatAny(value) {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

async function getQuantity(req) {
  const response = await api.post("vantage/quantity", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function getDescription(req) {
  const response = await api.post("vantage/description", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function getPartNumber(req) {
  const response = await api.post("vantage/partnumber", {
    jobNum: req.jobNum,
    seq: req.seq,
  });
  return response.data;
}

async function getPartNumberPoLine(req) {
  const response = await api.post("vantage/partnumber-po-line", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getQuantityPoLine(req) {
  const response = await api.post("vantage/quantity-po-line", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getDescriptionPoLine(req) {
  const response = await api.post("vantage/description-po-line", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getAssemblies(req) {
  const response = await api.post("vantage/assemblies", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getLots(req) {
  const response = await api.post("mafia/job-lots", {
    jobNum: req.jobNum,
  });
  return response.data;
}

async function getChildren(partnum) {
  const response = await api.get("mafia/children", {
    params: { partnum },
  });
  return response.data;
}

async function getJobRevMajor(jobnum, seq) {
  const response = await api.get("mafia/job-revmajor", {
    params: { jobnum, seq },
  });
  return response.data;
}

async function getJobRevMinor(jobnum, seq) {
  const response = await api.get("mafia/job-revminor", {
    params: { jobnum, seq },
  });
  return response.data;
}

async function getJobRevMajorComment(jobnum, seq) {
  const response = await api.get("mafia/job-revmajor-comment", {
    params: { jobnum, seq },
  });
  return response.data;
}

async function getJobRevMinorComment(jobnum, seq) {
  const response = await api.get("mafia/job-revminor-comment", {
    params: { jobnum, seq },
  });
  return response.data;
}

async function getMafiaTable(req) {
  const response = await api.post("mafia/mafiatable", {
    partNumber: req.partNumber,
    rev: req.rev,
    revMinor: req.revMinor,
  });
  return response.data;
}

async function getPirInstructions(req) {
  const response = await api.post("mafia/pir-instructions", {
    partNumber: req.partNumber,
    rev: req.rev,
    revMinor: req.revMinor,
  });
  return response.data;
}

async function getPirComments(req) {
  const response = await api.post("mafia/pir-comments", {
    jobNum: req.jobNum,
    revMajor: req.revMajor,
    revMinor: req.revMinor,
    wave: req.wave,
    seq: req.seq,
    partNum: req.partNum,
    orig: req.orig,
    lot: req.lot,
  });
  return response.data;
}

async function getPirEntries(req) {
  const response = await api.post("mafia/pir-entries", {
    jobNum: req.jobNum,
    revMajor: req.revMajor,
    revMinor: req.revMinor,
    wave: req.wave,
    seq: req.seq,
    partNum: req.partNum,
    orig: req.orig,
    lot: req.lot,
  });
  return response.data;
}

async function getJobNumberByPart(req) {
  const response = await api.post("vantage/job-by-part", {
    partNum: req.partNum,
  });
  return response.data;
}
// ── Round 58 (PIR fix): controlled, stage-aware error surfacing ──────────────
//
// Previously EVERY failure anywhere in the PIR build chain (a dozen backend
// calls) collapsed into the single generic dialog "Failed to load job
// information." — impossible to diagnose and, for Generate PIR, the rejection
// wasn't even caught (unhandled promise rejection in the console). Each build
// stage is now labelled, the backend's own error text is included, and every
// entry point catches — the page never blanks and all typed context is kept.

function extractApiError(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (error?.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }
  if (error?.response?.status) {
    return `Request failed with status ${error.response.status}.`;
  }
  return fallback;
}

async function callStage(stage, fn) {
  try {
    return await fn();
  } catch (error) {
    const detail = extractApiError(error, "request failed");
    const wrapped = new Error(`${stage} — ${detail}`);
    wrapped.isPirStageError = true;
    throw wrapped;
  }
}

function describePirError(error, fallback) {
  if (error?.isPirStageError) return error.message;
  const detail = extractApiError(error, "");
  return detail ? `${fallback} — ${detail}` : fallback;
}
